/* eslint-disable @typescript-eslint/no-empty-function */
import { LoadStatus } from "@/models/frontend-only/LoadStatus";
import { useI18n } from "vue-i18n";

export const useBigHeroBannerStore = defineStore("big-hero-banner", () => {
  const homeStore = useHomeStore();
  const profileStore = useProfileStore();
  const { t } = useI18n();

  const status = computed(() => {
    if (homeStore.status === LoadStatus.Loading) {
      return LoadStatus.Loading;
    } else if (homeStore.status === LoadStatus.Ready && banners.value.length) {
      return LoadStatus.Ready;
    } else {
      return LoadStatus.Error;
    }
  });

  const banners = computed(() => {
    const banners = [
      {
        id: "intro",
        type: "INTRO",
        url:
          profileStore.profile?.signUpUrl ||
          "https://profile.ccli.com/account/create?appContext=SongSelect&returnUrl=http%3a%2f%2fsongselect.ccli.com",
        img: "https://cdn.ccli.com/songselect/web/banner/global/en_US/ss-upgrade-banner-october-2023.jpg",
        text: {
          title: t("SV.LIT_SaveTimeLeadWell"),
          subtitle: t("SV.LIT_InstantlyTransposableSheetMusicForWorship"),
          lyrics: t("SV.LIT_Lyrics"),
          chord: t("SV.LIT_ChordSheets"),
          lead: t("SV.LIT_LeadSheets"),
          vocal: t("SV.LIT_VocalSheets"),
          cta: t("SV.LIT_SignUpForFree"),
        },
        ga: ["send", "event", "Home (Auth)", "Feature", "INTRO"],
      },
      ...(homeStore.data?.banners.filter((s) => s.type !== "INTRO") || []),
    ];

    const shuffledBanners = shuffle(
      banners.filter((s) => s.type === "ARTWORK"),
    );
    return [...banners.filter((s) => s.type !== "ARTWORK"), ...shuffledBanners];
  });

  const bannersInDOM = computed(() =>
    banners.value
      .map((b, i) => ({
        index: i,
        data: b,
      }))
      .filter(
        (b, i) =>
          i === currentSlideIndex.value || i === previousSlideIndex.value,
      ),
  );

  const currentSlide = computed(
    () => banners.value.find((b, i) => i === currentSlideIndex.value) || null,
  );
  const previousSlide = computed(
    () => banners.value.find((b, i) => i === previousSlideIndex.value) || null,
  );

  const currentSlideIndex = ref(0);
  const previousSlideIndex = ref(-1);
  const nextSlideIndex = ref(0);
  const transitioning = ref(false);
  const autoplay = ref(false);
  let timerRef: number | null = null;
  const initialized = ref(false);

  function autoplayInit() {
    if (initialized.value) {
      return;
    }
    initialized.value = true;
    startAutoplay();
  }

  function toggleAutoPlay() {
    if (autoplay.value) {
      stopAutoplay();
    } else {
      startAutoplay();
    }
  }

  function stopAutoplay() {
    autoplay.value = false;
    if (timerRef !== null) {
      window.clearInterval(timerRef);
      timerRef = null;
    }
  }

  function startAutoplay() {
    autoplay.value = true;
    if (timerRef !== null) {
      clearInterval(timerRef);
      timerRef = null;
    }

    timerRef = window.setTimeout(goToNextSlide, 4000);
  }

  async function triggerNextAutoPlay() {
    if (status.value !== LoadStatus.Ready) {
      await waitUntilTrue(() => status.value === LoadStatus.Ready);
    }

    if (!initialized.value) {
      autoplayInit();
      return;
    }

    if (timerRef !== null) {
      clearInterval(timerRef);
      timerRef = null;
    }

    if (autoplay.value) {
      timerRef = window.setTimeout(goToNextSlide, 4000);
    }
  }

  async function updateToSlide(index: number) {
    if (currentSlideIndex.value === index) {
      return;
    }
    nextSlideIndex.value = index;
    await nextTick;
    previousSlideIndex.value = currentSlideIndex.value;
    currentSlideIndex.value = nextSlideIndex.value;
  }

  async function updateToSlideAfterPreviousLoaded() {
    currentSlideIndex.value = nextSlideIndex.value;
  }

  function goToPreviousSlide() {
    let index = currentSlideIndex.value - 1;
    if (index < 0) {
      index = banners.value.length - 1;
    }
    updateToSlide(index);
  }

  const route = useRoute();

  function goToNextSlide() {
    if (route.name !== "Home") {
      return;
    }
    let index = currentSlideIndex.value + 1;
    if (index >= banners.value.length) {
      index = 0;
    }
    updateToSlide(index);
  }

  return {
    goToPreviousSlide,
    goToNextSlide,
    autoplay,
    toggleAutoPlay,
    startAutoplay,
    banners,
    currentSlideIndex,
    previousSlideIndex,
    transitioning,
    updateToSlide,
    status,
    bannersInDOM,
    currentSlide,
    triggerNextAutoPlay,
    previousSlide,
    updateToSlideAfterPreviousLoaded,
    nextSlideIndex,
    initialized,
  };
});
